import React, { Fragment, useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDebounceValue, useWindowSize } from 'usehooks-ts';
import * as S from './AdCards.styles';
import { getCardsCountInRow } from './AdCards.helpers';
import { AdCardsProps } from './AdCards.types';
import {
  DEFAULT_ROWS_COUNT_BEFORE_BANNER,
  ROWS_COUNT_BEFORE_BANNER,
} from './AdCards.constants';
import { AdCard } from '@/components/AdCard/AdCard';
import { generateRandomNumber, getDataStatAttr } from '@/utils/helpers';
import { Popup } from '@/components/UI/Popup/Popup';
import { useToggleOpenState } from '@/hooks/useToggleOpenState';
import { AdManagerBanner } from '@/components/Banners/AdManagerBanner';
import { BannerName } from '@/components/Banners/Banners.types';
import { Ad } from '@/api/types/common.types';
import { CommonTestIds } from '@/__e2e__/testIds/common';
import { AdsChunkSize } from '@/components/AdsWithInfiniteScroll/AdsWithInfiniteScroll.types';
import { useBridgeState } from '@/bridge/hooks';
import { AdStatus } from '@/types';
import { Toast } from '@/components/UI/Toast';
import { CollectionMasterSheet } from '@/features/BookmarkCollections/components';
import { useToastStore } from '@/store/useToastStore';

export const AdCards = React.memo(
  ({
    ads,
    listDataStat,
    hasActions,
    hasFavorite,
    hasAdManagerCard,
    isInitialLoading,
    chunkSize = AdsChunkSize.Small,
    isLoadingMore,
    bannerInList,
    isBannerInListAtTheEnd,
    isBannerInListAfterThreeRows,
    customStyles,
    hasFirstCardPriority,
  }: AdCardsProps) => {
    const { t } = useTranslation();
    const { width } = useWindowSize();
    const [debouncedWidth] = useDebounceValue(width, 200);
    const [isPopupOpen, popupToggle] = useToggleOpenState();
    const [selectedAdId, setSelectedAdId] = useState('');
    const { isWebView } = useBridgeState();
    const { openToast, closeToast, onAnimationComplete } = useToastStore();

    const [isCollectionMasterOpen, setIsCollectionMasterOpen] = useState(false);

    const handleAddToFavorites = useCallback((targetAd: Ad) => {
      closeToast();
      onAnimationComplete();

      setTimeout(() => {
        openToast({
          image: targetAd?.photo?.url,
          text: t('bookmark_collections.toast_tip'),
          action: t('bookmark_collections.toast_action'),
          onActionClick: handleToastActionClick,
        });
      }, 50);
    }, []);

    const handleRemoveFromFavorites = useCallback((targetAd: Ad) => {
      closeToast();
      onAnimationComplete();

      setTimeout(() => {
        openToast({
          image: targetAd?.photo?.url,
          text: t('bookmark_collections.unbookmarked'),
        });
      }, 50);
    }, []);

    const handleToastActionClick = useCallback(() => {
      closeToast();
      setIsCollectionMasterOpen(true);
    }, []);

    const onTriggerRemove = useCallback(
      (id: Ad['id']) => {
        setSelectedAdId(id);
        popupToggle();
      },
      [popupToggle]
    );

    const remove = useCallback(() => {
      setSelectedAdId('');
      popupToggle();
    }, [selectedAdId, popupToggle]);

    const isBannerRenders = useCallback(
      (idx: number): boolean => {
        const cardsCountInRow = getCardsCountInRow(debouncedWidth);
        const rowsBeforeBanner =
          isBannerInListAtTheEnd && ads?.length
            ? ads.length / cardsCountInRow
            : DEFAULT_ROWS_COUNT_BEFORE_BANNER;
        const adjustedIndex = isBannerInListAtTheEnd ? idx + 1 : idx;
        const isBannerPosition =
          adjustedIndex % (rowsBeforeBanner * cardsCountInRow) === 0 ||
          (!!isBannerInListAfterThreeRows &&
            cardsCountInRow * ROWS_COUNT_BEFORE_BANNER === adjustedIndex);
        return idx !== 0 && isBannerPosition && !!bannerInList;
      },
      [
        debouncedWidth,
        ads,
        isBannerInListAtTheEnd,
        isBannerInListAfterThreeRows,
        bannerInList,
      ]
    );

    const showAdmanagerBanner = useMemo(() => {
      return (
        typeof window !== 'undefined' &&
        window.innerWidth < 476 &&
        !isWebView &&
        hasAdManagerCard
      );
    }, [isWebView, hasAdManagerCard]);

    const dataStatAttr = useMemo(
      () => getDataStatAttr({ dataStat: listDataStat }),
      [listDataStat]
    );

    const popupActions = useMemo(
      () => ({
        left: { text: t('common.decline'), onClick: popupToggle },
        right: { text: t('common.remove'), isRed: true, onClick: remove },
      }),
      [t, popupToggle, remove]
    );

    const renderedAds = useMemo(() => {
      return ads?.map((item, idx) => {
        const { id, status } = item;
        const key = `${id}_${idx}`;
        const adCard = (
          <AdCard
            key={key}
            ad={item}
            hasActions={hasActions}
            hasFavorite={hasFavorite}
            onTriggerRemove={onTriggerRemove}
            onAddToFavorites={handleAddToFavorites}
            onRemoveFromFavorites={handleRemoveFromFavorites}
            priority={hasFirstCardPriority && !idx}
            {...(status === AdStatus.Expired && {
              overlayText: t('common.ad_inactive'),
            })}
          />
        );

        if (showAdmanagerBanner && idx === 3) {
          const banner = (
            <AdManagerBanner
              key={`${key}_banner`}
              bannerName={BannerName.Card}
            />
          );
          return (
            <Fragment key={`${key}_fragment`}>
              {banner}
              {adCard}
            </Fragment>
          );
        }

        if (isBannerRenders(showAdmanagerBanner ? idx + 1 : idx)) {
          const banner = (
            <S.BannerBox>{bannerInList?.(generateRandomNumber())}</S.BannerBox>
          );
          return (
            <Fragment key={`${key}_fragment`}>
              {isBannerInListAtTheEnd ? adCard : banner}
              {isBannerInListAtTheEnd ? banner : adCard}
            </Fragment>
          );
        }

        return adCard;
      });
    }, [
      ads,
      hasActions,
      hasFavorite,
      onTriggerRemove,
      handleAddToFavorites,
      hasFirstCardPriority,
      t,
      showAdmanagerBanner,
      isBannerRenders,
      bannerInList,
      isBannerInListAtTheEnd,
    ]);

    if (isInitialLoading) {
      return (
        <S.Box>
          {Array.from({ length: chunkSize }, (_, index) => (
            <AdCard key={index} isLoading />
          ))}
        </S.Box>
      );
    }

    if (!ads?.length) return null;

    const { paddingTop, paddingBottom } = customStyles || {};

    return (
      <>
        <S.Box
          $customPaddingTop={paddingTop}
          $customPaddingBottom={paddingBottom}
          {...dataStatAttr}
          data-testid={CommonTestIds.adCards.cardsBox}
        >
          {renderedAds}
          {isLoadingMore && <AdCard isLoading />}
        </S.Box>

        <CollectionMasterSheet
          isOpen={isCollectionMasterOpen}
          onClose={() => setIsCollectionMasterOpen(false)}
        />

        {hasActions && (
          <Popup
            isOpen={isPopupOpen}
            close={popupToggle}
            title={t('pages.cabinet.remove_ad_question')}
            description={t('pages.cabinet.remove_ad_popup_text')}
            isInPortal
            actions={popupActions}
          />
        )}

        <Toast />
      </>
    );
  }
);

AdCards.displayName = 'AdCards';
